import { observer } from 'mobx-react';
import React from 'react';
import { ModalBody } from 'reactstrap';
import DialogModal from '~/components/DialogModal';
import FullPageError from '~/components/FullPageError';
import WaitFor from '~/components/WaitFor';
import { PaypalProcessResult } from '~/lib/ApiModel';
import { getAppContext } from '~/lib/AppContext';
import { notFoundAsUndefined } from '~/lib/helper/RequestHelper';
import { ResourceType, resourceListRoute, serverRoute } from '~/lib/helper/RouteHelper';
import UnmountHelper from '~/lib/helper/UnmountHelper';
import { LoadStatus, LoadingTracker } from '~/lib/store/LoadingTracker';

interface ActionCompletePageProps {
    actionId?: string | null;
    /** PayPal's outcome, appended to the transaction success URL by components/paypal/Process and Cancel. */
    paypalResult?: string | null;
    completeTitle: string;
    errorTitle: string;
    errorMessage: string;
}

// Pending and cancelled PayPal returns reach this page too, through Continue in Process and Cancel, and
// announcing completion would contradict what those pages just told the customer. Neither message names
// a destination, since either return can end on the server list.
// Keyed by the generated union, so a value added by update-model fails to compile until it is given copy or undefined.
const PaypalResultCopy: Record<PaypalProcessResult, { title: string; message: string } | undefined> = {
    pending: {
        title: 'Payment Pending',
        message: 'Your PayPal payment has not cleared yet. Redirecting...',
    },
    cancelled: {
        title: 'Payment Cancelled',
        message: 'Your PayPal payment was cancelled. Redirecting...',
    },
    success: undefined,
};

function isPaypalProcessResult(value: string | null | undefined): value is PaypalProcessResult {
    return !!value && Object.keys(PaypalResultCopy).includes(value);
}

/**
 * Resolves an action to its server and sends the customer there, showing an interstitial
 * while it does. Mounted after any flow that returns from PayPal, so every failure has to land
 * somewhere useful rather than leaving the interstitial up.
 */
@observer
export default class ActionCompletePage extends React.Component<ActionCompletePageProps> {
    loader = new LoadingTracker();

    unmountHelper = new UnmountHelper(this);

    get paypalResult(): PaypalProcessResult | undefined {
        const value = this.props.paypalResult;
        return isPaypalProcessResult(value) ? value : undefined;
    }

    componentDidMount(): void {
        this.loadData();
    }

    loadData = () => {
        this.loader
            .track(async () => {
                await this.handleResult();
            })
            .catch((error) => {
                console.error(error);
            });
    };

    async handleResult() {
        const actionId = this.props.actionId?.trim();
        const numericActionId = actionId ? Number(actionId) : NaN;
        if (!Number.isFinite(numericActionId)) {
            this.goToServerList();
            return;
        }

        const action = await this.unmountHelper.cancelIfUnmounted(
            notFoundAsUndefined(() => getAppContext().apiClient.getAction(numericActionId))()
        );

        // Not-found is expected rather than exceptional: a finished or rolled-back action may already
        // have been cleaned up. An errored action has no server to show either.
        if (!action?.resource_id || action.status === 'errored') {
            this.goToServerList();
            return;
        }

        const serverId = action.resource_id;
        const server = await this.unmountHelper.cancelIfUnmounted(
            notFoundAsUndefined(() => getAppContext().apiClient.getServer(serverId))()
        );
        if (!server) {
            this.goToServerList();
            return;
        }

        // Replaces rather than pushes: Process arrives here via window.location.replace, so this
        // interstitial is the only panel entry in history. Pushing leaves Back returning to a page
        // that immediately re-resolves and navigates forward again.
        getAppContext().navigate(serverRoute(server), true);
    }

    goToServerList() {
        getAppContext().navigate(resourceListRoute(ResourceType.Server), true);
    }

    render() {
        const errored = this.loader.status === LoadStatus.Errored;
        const paypalResult = this.paypalResult;
        const paypalCopy = paypalResult ? PaypalResultCopy[paypalResult] : undefined;
        return (
            <DialogModal isOpen={true} centered labelledBy="action-complete-title">
                <ModalBody>
                    {/* Title sits outside the WaitFor so the labelled element exists in every
                        state (the dialog needs an accessible name even while loading). Its text
                        follows the load state so a failure doesn't read as a completion. */}
                    <div className="d-flex flex-column align-items-center text-center">
                        <h3 id="action-complete-title">
                            {errored ? this.props.errorTitle : (paypalCopy?.title ?? this.props.completeTitle)}
                        </h3>
                        <WaitFor
                            operation={this.loader}
                            errored={<FullPageError message={this.props.errorMessage} onRetry={this.loadData} />}
                        >
                            {() => <p>{paypalCopy?.message ?? 'Redirecting...'}</p>}
                        </WaitFor>
                    </div>
                </ModalBody>
            </DialogModal>
        );
    }
}
